import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Download,
  CheckCircle,
  ArrowRight,
  ExternalLink,
  Shield,
  Zap,
  Cpu,
  Star,
  Users,
  Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DownloadBackground } from '@/components/ui/DownloadBackground';

// Release configuration
const GITHUB_RELEASES_REPO = 'berkantkockaya/ochtopus-releases';
const CURRENT_VERSION = '1.0.5';
const RELEASE_TAG = 'v1.0.5';

// Download URLs from GitHub releases (public repo)
const DOWNLOAD_URLS = {
  macos: {
    arm64: `https://github.com/${GITHUB_RELEASES_REPO}/releases/download/${RELEASE_TAG}/Ochtopus_0.1.0_aarch64.dmg`,
  },
  windows: {
    msi: `https://github.com/${GITHUB_RELEASES_REPO}/releases/download/${RELEASE_TAG}/Ochtopus_0.1.0_x64_en-US.msi`,
    exe: `https://github.com/${GITHUB_RELEASES_REPO}/releases/download/${RELEASE_TAG}/Ochtopus_0.1.0_x64-setup.exe`,
  },
  linux: {
    appimage: `https://github.com/${GITHUB_RELEASES_REPO}/releases/download/${RELEASE_TAG}/Ochtopus_0.1.0_amd64.AppImage`,
    deb: `https://github.com/${GITHUB_RELEASES_REPO}/releases/download/${RELEASE_TAG}/Ochtopus_0.1.0_amd64.deb`,
  },
};

type Platform = 'macos' | 'windows' | 'linux' | 'unknown';

// OS Icons as SVG components
const AppleIcon = ({ size = 24, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const WindowsIcon = ({ size = 24, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M3 12V6.75l6-1.32v6.48L3 12zm17-9v8.75l-10 .15V5.21L20 3zM3 13l6 .09v6.81l-6-1.15V13zm17 .25V22l-10-1.91V13.1l10 .15z"/>
  </svg>
);

const LinuxIcon = ({ size = 24, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 266 312" fill="currentColor" className={className}>
    <path d="M128.6640625 79.2793c0 1-1 1-1 1h-1c-1 0-1-1-2-2 0 0-1-1-1-2s0-1 1-1l2 1c1 1 2 2 2 3m-18-10c0-5-2-8-5-8 0 0 0 1-1 1v2h3c0 2 1 3 1 5h2m35-5c2 0 3 2 4 5h2c-1-1-1-2-1-3s0-2-1-3-2-2-3-2c0 0-1 1-2 1 0 1 1 1 1 2m-30 16c-1 0-1 0-1-1s0-2 1-3c2 0 3-1 3-1 1 0 1 1 1 1 0 1-1 2-3 4h-1m-11-1c-4-2-5-5-5-10 0-3 0-5 2-7 1-2 3-3 5-3s3 1 5 3c1 3 2 6 2 9v2h1v-1c1 0 1-2 1-6 0-3 0-6-2-9s-4-5-8-5c-3 0-6 2-7 5-2 4-2.4 7-2.4 12 0 4 1.4 8 5.4 12 1-1 2-1 3-2m125 141c1 0 1-.4 1-1.3 0-2.2-1-4.8-4-7.7-3-3-8-4.9-14-5.7-1-.1-2-.1-2-.1-1-.2-1-.2-2-.2-1-.1-3-.3-4-.5 3-9.3 4-17.5 4-24.7 0-10-2-17-6-23s-8-9-13-10c-1 1-1 1-1 2 5 2 10 6 13 12 3 7 4 13 4 20 0 5.6-1 13.9-5 24.5-4 1.6-8 5.3-11 11.1 0 .9 0 1.4 1 1.4 0 0 1-.9 2-2.6 2-1.7 3-3.4 5-5.1 3-1.7 5-2.6 8-2.6 5 0 10 .7 13 2.1 4 1.3 6 2.7 7 4.3 1 1.5 2 2.9 3 4.2 0 1.3 1 1.9 1 1.9m-92-145c-1-1-1-3-1-5 0-4 0-6 2-9 2-2 4-3 6-3 3 0 5 2 7 4 1 3 2 5 2 8 0 5-2 8-6 9 0 0 1 1 2 1 2 0 3 1 5 2 1-6 2-10 2-15 0-6-1-10-3-13-3-3-6-4-10-4-3 0-6 1-9 3-2 3-3 5-3 8 0 5 1 9 3 13 1 0 2 1 3 1m12 16c-13 9-23 13-31 13-7 0-14-3-20-8 1 2 2 4 3 5l6 6c4 4 9 6 14 6 7 0 15-4 25-11l9-6c2-2 4-4 4-7 0-1 0-2-1-2-1-2-6-5-16-8-9-4-16-6-20-6-3 0-8 2-15 6-6 4-10 8-10 12 0 0 1 1 2 3 6 5 12 8 18 8 8 0 18-4 31-14v2c1 0 1 1 1 1m23 202c4 7.52 11 11.3 19 11.3 2 0 4-.3 6-.9 2-.4 4-1.1 5-1.9 1-.7 2-1.4 3-2.2 2-.7 2-1.2 3-1.7l17-14.7c4-3.19 8-5.98 13-8.4 4-2.4 8-4 10-4.9 3-.8 5-2 7-3.6 1-1.5 2-3.4 2-5.8 0-2.9-2-5.1-4-6.7s-4-2.7-6-3.4-4-2.3-7-5c-2-2.6-4-6.2-5-10.9l-1-5.8c-1-2.7-1-4.7-2-5.8 0-.3 0-.4-1-.4s-3 .9-4 2.6c-2 1.7-4 3.6-6 5.6-1 2-4 3.8-6 5.5-3 1.7-6 2.6-8 2.6-8 0-12-2.2-15-6.5-2-3.2-3-6.9-4-11.1-2-1.7-3-2.6-5-2.6-5 0-7 5.2-7 15.7v31.1c0 .9-1 2.9-1 6-1 3.1-1 6.62-1 10.6l-2 11.1v.17m-145-5.29c9.3 1.36 20 4.27 32.1 8.71 12.1 4.4 19.5 6.7 22.2 6.7 7 0 12.8-3.1 17.6-9.09 1-1.94 1-4.22 1-6.84 0-9.45-5.7-21.4-17.1-35.9l-6.8-9.1c-1.4-1.9-3.1-4.8-5.3-8.7-2.1-3.9-4-6.9-5.5-9-1.3-2.3-3.4-4.6-6.1-6.9-2.6-2.3-5.6-3.8-8.9-4.6-4.2.8-7.1 2.2-8.5 4.1s-2.2 4-2.4 6.2c-.3 2.1-.9 3.5-1.9 4.2-1 .6-2.7 1.1-5 1.6-.5 0-1.4 0-2.7.1h-2.7c-5.3 0-8.9.6-10.8 1.6-2.5 2.9-3.8 6.2-3.8 9.7 0 1.6.4 4.3 1.2 8.1.8 3.7 1.2 6.7 1.2 8.8 0 4.1-1.2 8.2-3.7 12.3-2.5 4.3-3.8 7.5-3.8 9.78 1 3.88 7.6 6.61 19.7 8.21m33.3-90.9c0-6.9 1.8-14.5 5.5-23.5 3.6-9 7.2-15 10.7-19-.2-1-.7-1-1.5-1l-1-1c-2.9 3-6.4 10-10.6 20-4.2 9-6.4 17.3-6.4 23.4 0 4.5 1.1 8.4 3.1 11.8 2.2 3.3 7.5 8.1 15.9 14.2l10.6 6.9c11.3 9.8 17.3 16.6 17.3 20.6 0 2.1-1 4.2-4 6.5-2 2.4-4.7 3.6-7 3.6-.2 0-.3.2-.3.7 0 .1 1 2.1 3.1 6 4.2 5.7 13.2 8.5 25.2 8.5 22 0 39-9 52-27 0-5 0-8.1-1-9.4v-3.7c0-6.5 1-11.4 3-14.6s4-4.7 7-4.7c2 0 4 .7 6 2.2 1-7.7 1-14.4 1-20.4 0-9.1 0-16.6-2-23.6-1-6-3-11-5-15l-6-9c-2-3-3-6-5-9-1-4-2-7-2-12-3-5-5-10-8-15-2-5-4-10-6-14l-9 7c-10 7-18 10-25 10-6 0-11-1-14-5l-6-5c0 3-1 7-3 11l-6.3 12c-2.8 7-4.3 11-4.6 14-.4 2-.7 4-.9 4l-7.5 15c-8.1 15-12.2 28.9-12.2 40.4 0 2.3.2 4.7.6 7.1-4.5-3.1-6.7-7.4-6.7-13m71.6 94.6c-13 0-23 1.76-30 5.25v-.3c-5 6-10.6 9.1-18.4 9.1-4.9 0-12.6-1.9-23-5.7-10.5-3.6-19.8-6.36-27.9-8.18-.8-.23-2.6-.57-5.5-1.03-2.8-.45-5.4-.91-7.7-1.37-2.1-.45-4.5-1.13-7.1-2.05-2.5-.79-4.5-1.82-6-3.07-1.38-1.26-2.06-2.68-2.06-4.27 0-1.6.34-3.31 1.02-5.13.64-1.1 1.34-2.2 2.04-3.2.7-1.1 1.3-2.1 1.7-3.1.6-.9 1-1.8 1.4-2.8.4-.9.8-1.8 1-2.9.2-1 .4-2 .4-3s-.4-4-1.2-9.3c-.8-5.2-1.2-8.5-1.2-9.9 0-4.4 1-7.9 3.2-10.4s4.3-3.8 6.5-3.8h11.5c.9 0 2.3-.5 4.4-1.7.7-1.6 1.3-2.9 1.7-4.1.5-1.2.7-2.1.9-2.5.2-.6.4-1.2.6-1.7.4-.7.9-1.5 1.6-2.3-.8-1-1.2-2.3-1.2-3.9 0-1.1 0-2.1.2-2.7 0-3.6 1.7-8.7 5.3-15.4l3.5-6.3c2.9-5.4 5.1-9.4 6.7-13.4 1.7-4 3.5-10 5.5-18 1.6-7 5.4-14 11.4-21l7.5-9c5.2-6 8.6-11 10.5-15s2.9-9 2.9-13c0-2-.5-8-1.6-18-1-10-1.5-20-1.5-29 0-7 .6-12 1.9-17s3.6-10 7-14c3-4 7-8 13-10s13-3 21-3c3 0 6 0 9 1 3 0 7 1 12 3 4 2 8 4 11 7 4 3 7 8 10 13 2 6 4 12 5 20 1 5 1 10 2 17 0 6 1 10 1 13 1 3 1 7 2 12 1 4 2 8 4 11 2 4 4 8 7 12 3 5 7 10 11 16 9 10 16 21 20 32 5 10 8 23 8 36.9 0 6.9-1 13.6-3 20.1 2 0 3 .8 4 2.2s2 4.4 3 9.1l1 7.4c1 2.2 2 4.3 5 6.1 2 1.8 4 3.3 7 4.5 2 1 5 2.4 7 4.2 2 2 3 4.1 3 6.3 0 3.4-1 5.9-3 7.7-2 2-4 3.4-7 4.3-2 1-6 3-12 5.82-5 2.96-10 6.55-15 10.8l-10 8.51c-4 3.9-8 6.7-11 8.4-3 1.8-7 2.7-11 2.7l-7-.8c-8-2.1-13-6.1-16-12.2-16-1.94-29-2.9-37-2.9"/>
  </svg>
);

interface PlatformInfo {
  id: Platform;
  name: string;
  icon: typeof AppleIcon;
  gradient: string;
  description: string;
  primaryDownload: { url: string; label: string; format: string; size: string };
  alternativeDownloads: { url: string; label: string; format: string; size: string }[];
  requirements: string[];
}

// Detect user's operating system
function detectOS(): Platform {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform.toLowerCase();

  if (platform.includes('mac') || userAgent.includes('mac')) {
    return 'macos';
  }
  if (platform.includes('win') || userAgent.includes('win')) {
    return 'windows';
  }
  if (platform.includes('linux') || userAgent.includes('linux')) {
    return 'linux';
  }
  return 'unknown';
}

// Platform configurations
const platforms: Record<Exclude<Platform, 'unknown'>, PlatformInfo> = {
  macos: {
    id: 'macos',
    name: 'macOS',
    icon: AppleIcon,
    gradient: 'from-gray-600 to-gray-800',
    description: 'Apple Silicon & Intel',
    primaryDownload: {
      url: DOWNLOAD_URLS.macos.arm64,
      label: 'macOS için İndir',
      format: '.dmg',
      size: '~120 MB',
    },
    alternativeDownloads: [],
    requirements: [
      'macOS 11 (Big Sur) veya üstü',
      'Apple Silicon veya Intel işlemci',
      'En az 4 GB RAM',
      '500 MB boş disk alanı',
    ],
  },
  windows: {
    id: 'windows',
    name: 'Windows',
    icon: WindowsIcon,
    gradient: 'from-blue-500 to-blue-700',
    description: 'Windows 10/11 (64-bit)',
    primaryDownload: {
      url: DOWNLOAD_URLS.windows.exe,
      label: 'Windows için İndir',
      format: '.exe',
      size: '~100 MB',
    },
    alternativeDownloads: [
      {
        url: DOWNLOAD_URLS.windows.msi,
        label: 'MSI Yükleyici',
        format: '.msi',
        size: '~100 MB',
      },
    ],
    requirements: [
      'Windows 10 (64-bit) veya üstü',
      'En az 4 GB RAM',
      '500 MB boş disk alanı',
      'WebView2 Runtime',
    ],
  },
  linux: {
    id: 'linux',
    name: 'Linux',
    icon: LinuxIcon,
    gradient: 'from-orange-500 to-yellow-600',
    description: 'Ubuntu, Fedora, Arch',
    primaryDownload: {
      url: DOWNLOAD_URLS.linux.appimage,
      label: 'Linux için İndir',
      format: '.AppImage',
      size: '~130 MB',
    },
    alternativeDownloads: [
      {
        url: DOWNLOAD_URLS.linux.deb,
        label: 'Debian/Ubuntu',
        format: '.deb',
        size: '~90 MB',
      },
    ],
    requirements: [
      'Ubuntu 20.04+ veya benzeri',
      'GTK 3 ve WebKit2GTK',
      'En az 4 GB RAM',
      '500 MB boş disk alanı',
    ],
  },
};

// Stats
const stats = [
  { icon: Users, value: '500K+', label: 'Geliştirici' },
  { icon: Star, value: '4.9', label: 'Ortalama Puan' },
  { icon: Clock, value: '<1dk', label: 'Kurulum Süresi' },
];

// Features
const features = [
  {
    icon: Zap,
    title: 'Anında Başlayın',
    description: 'Tek tıkla kurulum, saniyeler içinde kodlamaya başlayın',
  },
  {
    icon: Shield,
    title: 'Güvenli & İmzalı',
    description: 'Kod imzalı ve güvenlik taramasından geçmiş',
  },
  {
    icon: Cpu,
    title: 'Hafif & Hızlı',
    description: 'Düşük kaynak kullanımı, yüksek performans',
  },
];

export function DownloadPage() {
  const { t } = useTranslation();
  const [detectedOS, setDetectedOS] = useState<Platform>('unknown');
  const [selectedPlatform, setSelectedPlatform] = useState<Exclude<Platform, 'unknown'>>('macos');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const os = detectOS();
    setDetectedOS(os);
    setSelectedPlatform(os === 'unknown' ? 'macos' : os);
  }, []);

  const currentPlatform = platforms[selectedPlatform];

  const handleDownload = () => {
    setIsDownloading(true);
    setTimeout(() => setIsDownloading(false), 2000);
  };

  return (
    <>
      <SEO {...SEO_CONFIG.download} />
      <div className="min-h-screen relative">
      {/* Background Animation */}
      <DownloadBackground />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20">
        <div className="container relative z-10">
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm font-medium mb-6">
              <Download size={16} />
              Ücretsiz İndir
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6">
              Yapay Zeka Destekli{' '}
              <span className="gradient-text">Kod Editörü</span>
            </h1>
            <p className="text-lg md:text-xl text-text-secondary max-w-2xl mx-auto">
              Ochtopus IDE ile kodlama deneyiminizi bir üst seviyeye taşıyın.
              Hemen indirin, kurulum bir dakikadan kısa sürer.
            </p>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 mb-16">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <stat.icon size={20} className="text-accent" />
                </div>
                <div>
                  <div className="text-xl font-bold text-text-primary">{stat.value}</div>
                  <div className="text-sm text-text-tertiary">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Platform Selector */}
          <div className="max-w-4xl mx-auto">
            {/* OS Tabs */}
            <div className="flex justify-center gap-2 mb-8">
              {(Object.keys(platforms) as Array<Exclude<Platform, 'unknown'>>).map((platformId) => {
                const platform = platforms[platformId];
                const isSelected = selectedPlatform === platformId;
                const isDetected = detectedOS === platformId;

                return (
                  <button
                    key={platformId}
                    onClick={() => setSelectedPlatform(platformId)}
                    className={`relative flex items-center gap-3 px-6 py-3 rounded-xl transition-all ${
                      isSelected
                        ? 'bg-accent text-white shadow-lg shadow-accent/30'
                        : 'bg-bg-surface hover:bg-bg-elevated text-text-secondary hover:text-text-primary border border-border-default'
                    }`}
                  >
                    <platform.icon size={24} />
                    <span className="font-medium">{platform.name}</span>
                    {isDetected && !isSelected && (
                      <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-success flex items-center justify-center">
                        <CheckCircle size={12} className="text-white" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Main Download Card */}
            <div className="relative">
              {/* Recommended Badge - Better positioned */}
              {detectedOS !== 'unknown' && detectedOS === selectedPlatform && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-success text-white text-sm font-medium shadow-lg shadow-success/30">
                    <CheckCircle size={16} />
                    Sisteminiz için Önerildi
                  </div>
                </div>
              )}

              <div className="card p-8 md:p-10 border-2 border-accent/30 bg-gradient-to-br from-bg-surface to-bg-elevated relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-accent/10 to-transparent rounded-full blur-3xl" />

                <div className="relative z-10">
                  {/* Platform Header */}
                  <div className="flex flex-col md:flex-row items-center gap-6 mb-8">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${currentPlatform.gradient} flex items-center justify-center shadow-xl`}>
                      <currentPlatform.icon size={40} className="text-white" />
                    </div>
                    <div className="text-center md:text-left">
                      <h2 className="text-3xl font-bold text-text-primary mb-1">
                        {currentPlatform.name} için Ochtopus
                      </h2>
                      <p className="text-text-secondary">{currentPlatform.description}</p>
                    </div>
                  </div>

                  {/* Download Button */}
                  <a
                    href={currentPlatform.primaryDownload.url}
                    onClick={handleDownload}
                    className={`btn btn-primary btn-lg w-full justify-center text-lg py-4 mb-4 ${
                      isDownloading ? 'animate-pulse' : ''
                    }`}
                  >
                    <Download size={24} className={isDownloading ? 'animate-bounce' : ''} />
                    {isDownloading ? 'İndiriliyor...' : currentPlatform.primaryDownload.label}
                    <span className="px-2 py-1 rounded bg-white/20 text-sm ml-2">
                      {currentPlatform.primaryDownload.format}
                    </span>
                  </a>

                  {/* Download Info */}
                  <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-text-tertiary mb-6">
                    <span>Boyut: {currentPlatform.primaryDownload.size}</span>
                    <span>•</span>
                    <span>Sürüm: {CURRENT_VERSION}</span>
                    <span>•</span>
                    <Link
                      to="/changelog"
                      className="flex items-center gap-1 text-accent hover:underline"
                    >
                      Sürüm Notları
                      <ExternalLink size={12} />
                    </Link>
                  </div>

                  {/* Alternative Downloads */}
                  {currentPlatform.alternativeDownloads.length > 0 && (
                    <div className="pt-4 border-t border-border-default">
                      <p className="text-sm text-text-tertiary text-center mb-3">Alternatif İndirme Seçenekleri</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {currentPlatform.alternativeDownloads.map((download) => (
                          <a
                            key={download.label}
                            href={download.url}
                            className="btn btn-secondary text-sm"
                          >
                            {download.label}
                            <span className="text-xs opacity-70 ml-1">({download.format})</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* System Requirements - Collapsible style */}
              <details className="mt-4 group">
                <summary className="flex items-center justify-center gap-2 text-sm text-text-secondary cursor-pointer hover:text-text-primary transition-colors py-2">
                  <span>Sistem Gereksinimleri</span>
                  <ArrowRight size={14} className="transform group-open:rotate-90 transition-transform" />
                </summary>
                <div className="mt-2 p-4 rounded-lg bg-bg-surface border border-border-default">
                  <ul className="grid md:grid-cols-2 gap-2">
                    {currentPlatform.requirements.map((req) => (
                      <li key={req} className="flex items-center gap-2 text-sm text-text-secondary">
                        <CheckCircle size={14} className="text-success flex-shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-20 bg-bg-surface/50">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {features.map((feature) => (
              <div key={feature.title} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-accent/30">
                  <feature.icon size={28} className="text-white" />
                </div>
                <h3 className="font-semibold text-text-primary text-lg mb-2">{feature.title}</h3>
                <p className="text-text-secondary">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Guide */}
      <section className="relative py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
              Hızlı Kurulum <span className="gradient-text">Rehberi</span>
            </h2>

            <div className="space-y-4">
              {/* macOS */}
              <div className="card p-6 flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center flex-shrink-0">
                  <AppleIcon size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-2">macOS</h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-text-secondary">
                    <li>DMG dosyasını indirin ve açın</li>
                    <li>Ochtopus.app'i Uygulamalar klasörüne sürükleyin</li>
                    <li>İlk açılışta Control + tıklama yapıp "Aç" seçin</li>
                  </ol>
                  <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-xs text-amber-400 font-medium mb-1">⚠️ "Uygulama hasarlı" uyarısı alırsanız:</p>
                    <code className="block text-xs text-amber-300/80 bg-bg-elevated p-2 rounded">
                      xattr -cr /Applications/Ochtopus.app
                    </code>
                  </div>
                </div>
              </div>

              {/* Windows */}
              <div className="card p-6 flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center flex-shrink-0">
                  <WindowsIcon size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-2">Windows</h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-text-secondary">
                    <li>EXE veya MSI yükleyicisini indirin</li>
                    <li>Yükleyiciyi çalıştırın ve talimatları izleyin</li>
                    <li>Kurulum tamamlandığında Ochtopus'u başlatın</li>
                  </ol>
                </div>
              </div>

              {/* Linux */}
              <div className="card p-6 flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
                  <LinuxIcon size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-2">Linux</h3>
                  <div className="space-y-3 text-sm text-text-secondary">
                    <div>
                      <p className="font-medium text-text-primary mb-1">AppImage:</p>
                      <code className="block bg-bg-elevated p-2 rounded text-xs">
                        chmod +x Ochtopus_*.AppImage && ./Ochtopus_*.AppImage
                      </code>
                    </div>
                    <div>
                      <p className="font-medium text-text-primary mb-1">Debian/Ubuntu:</p>
                      <code className="block bg-bg-elevated p-2 rounded text-xs">
                        sudo dpkg -i ochtopus_*.deb
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-bg-surface/50">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Yardıma mı ihtiyacınız var?</h2>
            <p className="text-text-secondary mb-8">
              Kurulum veya kullanım hakkında sorularınız için dokümantasyonumuzu inceleyin
              veya topluluk forumlarımızı ziyaret edin.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/docs" className="btn btn-primary">
                Dokümantasyon
                <ArrowRight size={18} />
              </Link>
              <Link to="/community" className="btn btn-secondary">
                Topluluk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
